import React from 'react';
import { useTranslation } from 'react-i18next';
import MoreVertMenu from 'components/@common/MoreVertMenu';
import ProductIconList from 'components/@common/ProductIconList';
import StatusChip from 'components/@common/StatusChip';
import Avatar from '@material-ui/core/Avatar';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { ActionOption } from 'types';
import { User } from 'types/schema-generated-types';
import { formatDateString } from 'utilities/date-utils';
import { getInitials, getFullName } from 'utilities/name-utils';
import { commaDelimit } from 'utilities/string-utils';
import getOrgAssociationDetails from '../utils/get-org-association-details';
import BreakableTypography from 'components/@common/BreakableTypography';

const useStyles = makeStyles(theme => {
  return {
    container: {
      padding: theme.spacing(3),
      paddingTop: theme.spacing(2)
    },
    headerContainer: {
      marginBottom: theme.spacing()
    },
    avatar: {
      height: 44,
      width: 44
    },
    nameContainer: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    vertMenuContainer: {
      marginTop: -1 * (theme.spacing(3) / 2),
      marginRight: -1 * theme.spacing(4)
    },
    titleContainer: {
      display: 'flex',
      flexDirection: 'column'
    },
    labelText: {
      color: theme.palette.grey[700]
    },
    informationContainer: {},
    descriptionContainer: {
      marginTop: theme.spacing()
    }
  };
});

interface UserCardProps {
  user: User;
  actionOptions?: ActionOption[];
}

const UserCard: React.FC<UserCardProps> = ({ user, actionOptions }) => {
  const classes = useStyles();
  const { t } = useTranslation('UserCard');

  const orgDetails = getOrgAssociationDetails(user);
  const userOrgRoles = commaDelimit(orgDetails.organizationRoleNames);

  return (
    <Paper className={classes.container}>
      <div className={classes.headerContainer}>
        <Grid container spacing={2}>
          <Grid item>
            <Avatar alt={getFullName(user)} className={classes.avatar} src={user.avatarUrl || ''}>
              {getInitials(user)}
            </Avatar>
          </Grid>
          <Grid item xs={true}>
            <div className={classes.nameContainer}>
              <div>
                <Typography variant="h4">{getFullName(user)}</Typography>
              </div>
              <div className={classes.vertMenuContainer}>
                <MoreVertMenu
                  actionOptions={actionOptions ?? []}
                  id={user.id}
                  context={{
                    organizationId: user.organizationAssociations?.map(a => a.organization.id)
                  }}
                />
              </div>
            </div>
            <div className={classes.titleContainer}>
              <div>
                <Typography variant="subtitle2" className={classes.labelText}>
                  {userOrgRoles}
                </Typography>
              </div>
              <StatusChip status={user.status} size="small" />
            </div>
          </Grid>
        </Grid>
      </div>
      <Divider />
      <div className={classes.informationContainer}>
        <div className={classes.descriptionContainer}>
          <Typography variant="subtitle2" className={classes.labelText}>
            {t('Email', { ns: '@common' })}
          </Typography>
          <BreakableTypography>{user.username}</BreakableTypography>
        </div>
        <div className={classes.descriptionContainer}>
          <Typography variant="subtitle2" className={classes.labelText}>
            {t('Last Activity')}
          </Typography>
          <Typography>{formatDateString(new Date(user.dateUpdated))}</Typography>
        </div>
        <div className={classes.descriptionContainer}>
          <Typography variant="subtitle2" className={classes.labelText}>
            {t('Educator PIN')}
          </Typography>
          <Typography>{user.pin}</Typography>
        </div>
        <div className={classes.descriptionContainer}>
          <Typography variant="subtitle2" className={classes.labelText}>
            {t('Products Used')}
          </Typography>
          <div>
            <ProductIconList style={{ minWidth: 120 }} licenseUsage={user.licenseUsage} />
          </div>
        </div>
      </div>
    </Paper>
  );
};

export default UserCard;
